<template>
  <div class="row">
    <div class="col-12">
      <page-header :actions="headerActionsConfig" :page="headerConfig" />
    </div>
    <div class="col-12">
      <board :details="studentBoardDetails" header-class="pt-2 px-1" />
      <board
        class="custom-board-prov-remedy pl-4"
        :details="requestBoardDetails"
        header-class="'px-1"
        row-class="py-0"
      />
    </div>
    <custom-tab class="w-100 custom-tab-prov-remedy">
      <custom-tab-item
        :active="tabIndex === GeneralInformationTabIndex"
        :lazy="false"
        title="Información general de la solicitud"
        @click="onChangeInfoTab(GeneralInformationTabIndex)"
      >
        <validation-observer
          ref="provisionalRemedyRequestRef"
          v-slot="{}"
          tag="form"
        >
          <div class="col-12">
            <RequestGeneralInfo
              v-model="model.generalInformation"
              :applicant-categories="applicantCategories"
              :is-read-only="isReadOnly"
            />
            <RequestContactInfo
              v-model="model.contactInformation"
              :is-read-only="isReadOnly"
            />
            <RequestPostalAddressInfo
              v-model="model.postalAddress"
              :is-read-only="isReadOnly"
              :municipalities="municipalities"
            />
            <RequestDocuments
              v-model="model.documents"
              :document-types="documentTypes"
              :is-read-only="isReadOnly"
              @on-download="onDownloadHandler"
              @on-file-deleted="onFileDeletedHandler"
            />
            <validation-provider
              v-slot="{ errors }"
              :rules="{ required: _needRequiredDocuments }"
            >
              <custom-error
                v-if="errors[0]"
                class="mt-2"
                message="Es requerido subir una minuta para la terapia que cuente con servicios de visitas compensada."
                variant="no-border"
              />
            </validation-provider>

            <RequestEducationalServiceInfo
              v-model="model.studentInformation.location"
              :default-value="locationDefaultValue"
              :is-read-only="isReadOnly"
              :location-types="locationTypes"
            />
            <RequestAdditionalInfo
              v-model="model.generalInformation.additionalComments"
              :is-read-only="isReadOnly"
            />
          </div>
        </validation-observer>
      </custom-tab-item>
      <custom-tab-item
        :active="tabIndex === ServicesTabIndex"
        :lazy="false"
        title="Servicios a solicitar"
        @click="onChangeInfoTab(ServicesTabIndex)"
      >
        <validation-observer
          ref="provisionalRemedyServicesRef"
          v-slot="{}"
          tag="form"
        >
          <request-service
            ref="requestServiceRef"
            :is-read-only="hasRequest"
            :request-services="requestServices"
            :student-sie-id="studentSieId"
            :un-available-services="unAvailableServices"
          />
        </validation-observer>
      </custom-tab-item>
    </custom-tab>
  </div>
</template>

<script>
import objectToFormData from 'object-to-formdata';
import PageHeader from 'PageHeader';
import Board from 'Board';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import { ValidationObserver } from 'vee-validate';
import {
  getRequestInfo,
  saveRequest as _saveRequest,
} from '@/services/uspqrp/provisional-remedy.api';
import { downloadDocumentByAttachmentId as _downloadDocumentByAttachmentId } from '@/services/common/document.api.js';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import RequestStatuses from '@/utils/constants/requestStatuses';
import ServiceTypes from '@/utils/constants/serviceTypes';
import RequestRemedyTabs from '@/utils/constants/requestRemedyTabs';
import loadingMixin from '@quisit/mipe-utils/mixins/loading';

import {
  RequestGeneralInfo,
  RequestContactInfo,
  RequestPostalAddressInfo,
  RequestEducationalServiceInfo,
  RequestService,
  RequestAdditionalInfo,
  RequestDocuments,
} from './components';
import documentTypes from '@/utils/constants/documentTypes';
import CustomError from 'custom-error';

const REQUEST_MANAGEMENT_ROUTE_NAME = 'prov-remedy-req-management-add';

export default {
  components: {
    PageHeader,
    CustomTab,
    CustomTabItem,
    Board,
    ValidationObserver,
    RequestGeneralInfo,
    RequestContactInfo,
    RequestPostalAddressInfo,
    RequestDocuments,
    RequestEducationalServiceInfo,
    RequestService,
    RequestAdditionalInfo,
    CustomError,
  },
  mixins: [alertMixin, loadingMixin],
  data: () => ({
    tabIndex: RequestRemedyTabs.GENERAL_INFORMATION,
    applicantCategories: [],
    municipalities: [],
    locationTypes: [],
    documentTypes: [],
    unAvailableServices: [],
    requestServices: [],
    needRequiredDocuments: false,
    model: {
      generalInformation: {
        requestDate: null,
        applicantName: null,
        unknownApplicantNameFlag: false,
        personInChargeName: null,
        applicantPositionId: null,
      },
      contactInformation: {
        contactName: null,
        email: null,
        cellPhone: null,
        homePhone: null,
        workPhone: null,
        extension: null,
      },
      postalAddress: {
        addressLine1: null,
        addressLine2: null,
        municipalityId: null,
        state: null,
        postalCode: null,
      },
      studentInformation: {
        studentName: null,
        regStudent: null,
        impedimentDescription: null,
        impedimentId: null,
        isStudentEgressed: true,
        location: {
          locationType: { name: '' },
          region: { name: '' },
          district: { name: '' },
          municipality: { name: '' },
          school: { name: '' },
          name: null,
        },
      },
      documents: [],
      request: {},
    },
    deletedFiles: [],
  }),
  computed: {
    GeneralInformationTabIndex() {
      return RequestRemedyTabs.GENERAL_INFORMATION;
    },
    ServicesTabIndex() {
      return RequestRemedyTabs.SERVICES;
    },
    headerConfig() {
      return {
        icon: 'fas fa-clipboard-list',
        title: 'Solicitud de Remedio Provisional',
        routes: [
          'Inicio',
          'USPQRP',
          'Solicitud de Remedio Provisional',
          this.routeName,
        ],
      };
    },
    routeName() {
      return this.isReadOnly ? 'Ver' : 'Añadir';
    },
    headerActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left fa-fw',
          variant: 'outline-success',
          click: () =>
            this.$router.push({ name: 'prov-remedy-req-management' }),
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag pr-2',
          variant: 'success',
          visible:
            !this.isReadOnly &&
            !this.model.studentInformation.isStudentEgressed,
          click: () => this.onSubmitHandler(),
        },
      ];
    },
    studentBoardDetails() {
      return [
        [
          {
            label: 'Estudiante:',
            data: this.model.studentInformation?.studentName,
            icon: 'fas fa-address-book',
            cols: 'col-12 col-lg-4 w-50',
          },
          {
            label: 'Número de Registro:',
            data: this.model.studentInformation?.regStudent,
            icon: 'fas fa-address-book',
            cols: 'col-12 col-lg-4 w-50',
          },
          {
            label: 'Impedimento:',
            data: this.model.studentInformation?.impedimentDescription,
            icon: 'fas fa-ballot-check',
            cols: 'col-12 col-lg-4 w-50',
          },
        ],
      ];
    },
    requestBoardDetails() {
      return [
        [
          {
            label: 'Número de Solicitud:',
            data: this.isRequestStatusNew
              ? '-'
              : this.model.generalInformation?.requestNumber,
            cols: 'col-12 col-lg-4 w-50 pl-4',
          },
          {
            label: 'Fecha de Solicitud:',
            data: this.isRequestStatusNew
              ? '-'
              : this.$options.filters.date(
                  this.model.generalInformation?.requestDate
                ),
            cols: 'col-12 col-lg-4 w-50 pl-4',
          },
          {
            label: 'Estatus:',
            data: this.model.generalInformation?.requestStatusDescription,
            cols: 'col-lg-4 w-50 pl-4',
          },
        ],
      ];
    },
    isRequestStatusNew() {
      if (this.model.generalInformation?.requestStatusId === 0) return true;
      return (
        this.model.generalInformation?.requestStatusId === RequestStatuses.New
      );
    },
    locationDefaultValue() {
      return this.model.studentInformation.location;
    },
    requestId() {
      return Number(this.$route.params.requestId) || null;
    },
    isReadOnly() {
      return this.hasRequest ? !this.canEditRequest : false;
    },
    hasRequest() {
      return this.$route.params.requestId ||
        this.model?.generalInformation?.requestId
        ? true
        : false;
    },
    requestServiceRef() {
      return this.$refs.requestServiceRef;
    },
    getServiceModel() {
      return (service, idx, getCatalogOption = false) => {
        if (service.component != undefined) {
          const component = this.requestServiceComponentRef(
            service.component,
            idx
          );
          return {
            [`requestService${service.component}`]: {
              ...component?.getModel(getCatalogOption),
              disciplineType: component?.model?.discipline,
            },
          };
        }
      };
    },
    studentSieId() {
      return +this.$route.params?.studentSieId;
    },
    _needRequiredDocuments() {
      return this.needRequiredDocuments;
    },
    editApplyStatus() {
      return [
        RequestStatuses.Submitted,
        RequestStatuses.Approved,
        RequestStatuses.Closed,
      ];
    },
    canEditRequest() {
      return (
        this.editApplyStatus.includes(
          this.model?.generalInformation?.requestStatusId
        ) && this.userHasPermissions('rempro.uspqrp.editgeneralinformation')
      );
    },
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.path !== from.path) {
      next();
      await this.onLoad(to.params.studentSieId, to.params.requestId);
    }
  },
  created() {
    this.ShowWait();
  },
  async mounted() {
    await this.onLoad();
  },
  methods: {
    onAddRequestDocument(item) {
      this.requestDocument = item;
    },
    async onLoad(studentSieId = null, requestId = null) {
      await this.getGeneralInformation(
        studentSieId ?? this.$route.params?.studentSieId,
        this.requestId || requestId
      );
    },
    async getGeneralInformation(studentSieId, requestId = null) {
      const { data } = await getRequestInfo(studentSieId, requestId).catch(
        (err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
          this.HideWait();
        }
      );
      this.setCollections(data.collections);
      this.requestServices = data.requestServices;
      this.model = {
        ...data.requestInformation,
        postalAddress: {
          ...data.requestInformation?.postalAddress,
          state: data.requestInformation?.postalAddress?.state || 'PR',
        },
        ...data.request,
      };

      if (this.model.studentInformation.isStudentEgressed) {
        this.ShowToast(
          'Estudiante egresado',
          'El estudiante seleccionado no es elegible ya que se encuentra egresado.',
          'error',
          () => {},
          0
        );
      }

      this.HideWait();
    },
    onChangeInfoTab(index) {
      this.tabIndex = index;
    },
    async hasRequiredDocuments() {
      const selectedServices = await this.getSelectedServices();

      const hasCompensatoryService =
        selectedServices
          .filter((x) => x.serviceTypeId == ServiceTypes.Therapy)
          .findIndex((x) => x.requestServiceTherapy.isCompensatory) != -1;

      const hasMemorandum =
        this.model.documents.findIndex(
          (x) => x.documentTypeId == documentTypes.Memorandum
        ) != -1;
      this.needRequiredDocuments = hasCompensatoryService && !hasMemorandum;

      return !this.needRequiredDocuments;
    },
    async onSubmitHandler() {
      const isDocumentsValid = await this.hasRequiredDocuments();
      const isFormValid = await this.$refs.provisionalRemedyRequestRef.validate();
      const isServicesValid = await this.$refs.provisionalRemedyServicesRef.validate();
      const selectedServices = await this.getSelectedServices();

      if (!isFormValid || !isDocumentsValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        this.onChangeInfoTab(RequestRemedyTabs.GENERAL_INFORMATION);
        return;
      }

      if (selectedServices.length == 0) {
        this.ShowToast('Alerta', 'Favor añadir al menos un servicio.', 'error');
        this.onChangeInfoTab(RequestRemedyTabs.SERVICES);
        return;
      }

      if (!isServicesValid && !this.hasRequest) {
        this.ShowToast(
          'Alerta',
          'Favor completar los servicios a solicitar.',
          'error'
        );
        this.onChangeInfoTab(RequestRemedyTabs.SERVICES);
        return;
      }

      const {
        studentInformation,
        generalInformation,
        contactInformation,
        postalAddress,
        documents,
      } = this.model;

      let documentsPayload = documents.map((x) => ({
        file: x.attachment,
        documentTypeId: x.documentTypeId,
        documentId: x?.documentId ?? null,
        isDeleted: false,
      }));

      if (this.hasRequest && this.deletedFiles.length > 0) {
        documentsPayload = documentsPayload.concat(
          this.deletedFiles.map((x) => ({
            file: x.attachment,
            documentTypeId: x.documentTypeId,
            documentId: x.documentId,
            isDeleted: true,
          }))
        );
      }

      const payload = {
        //general info
        isEditing: this.hasRequest,
        requestId: this.requestId,
        studentSieId: studentInformation.studentSieId,
        requestDate: generalInformation.requestDate,
        applicantName: generalInformation.applicantName,
        unknownApplicantNameFlag:
          generalInformation.unknownApplicantNameFlag || false,
        applicantPositionId: generalInformation.applicantPositionId,
        personInChargeName:
          generalInformation.personInChargeName ??
          contactInformation.contactName,

        //contact info
        contactName: contactInformation.contactName,
        applicantEmail: contactInformation.email,
        contactCellPhoneNumber: contactInformation.cellPhone,
        contactResidentialPhoneNumber: contactInformation.homePhone,
        contactWorkPhoneNumber: contactInformation.workPhone,
        contactWorkPhoneExtension: contactInformation.extension,

        //address
        postalAddressLine1: postalAddress.addressLine1,
        postalAddressLine2: postalAddress.addressLine2,
        postalAddressZipCode: postalAddress.postalCode,
        postalAddressCityId: postalAddress.municipalityId,
        city: postalAddress.state,

        //documents
        documents: documentsPayload,

        //educational service info
        locationTypeId: studentInformation.location.locationType?.id,
        municipalityId: studentInformation.location.municipality?.id,
        regionId: studentInformation.location.region?.id,
        districtId: studentInformation.location.district?.id,
        locationSchoolId: studentInformation.location.school?.id,
        centerId: studentInformation.location.center?.id,
        privateLocationName: studentInformation.location.privateLocationName,

        //additional info
        additionalInformation: generalInformation.additionalComments,

        //services
        services: selectedServices,
      };

      const params = objectToFormData(
        {
          ...payload,
        },
        { indices: true, nullsAsUndefineds: true }
      );

      documentsPayload.forEach((document, index) => {
        params.append(`documents[${index}].file`, document.file);
        params.append(
          `documents[${index}].documentTypeId`,
          document.documentTypeId
        );
      });

      const { data: result } = await _saveRequest(params).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
      });

      if (result?.requestId && result?.studentSieId > 0) {
        //Validation of error Code
        let message = 'La solicitud ha sido sometida exitosamente.';
        if (result?.messageValidationCode == 500) {
          message +=
            ' Sin embargo, debido a que tiene permiso especial ' +
            result?.message;
        }
        this.ShowToast('Proceso Exitoso', message, 'success');

        this.$router.push({
          name: REQUEST_MANAGEMENT_ROUTE_NAME,
          params: {
            studentSieId: result.studentSieId,
            requestId: result.requestId,
          },
        });
      }
    },
    async getSelectedServices() {
      const params = [];
      let validModel = true;

      const selectedServices = this.requestServiceRef.selectedServices;

      for (const [i, s] of selectedServices.entries()) {
        if (
          [ServiceTypes.Therapy].includes(s.serviceId) &&
          !(await this.requestServiceRef.$refs[
            `${s.component}-${i}`
          ][0].validateService())
        ) {
          validModel = false;
        }
        let serviceModel = this.getServiceModel(s, i);
        if (serviceModel != undefined) {
          params.push({
            requestServiceId: s.requestServiceId,
            requestId: this.requestId,
            serviceTypeId: s.serviceId,
            ...serviceModel,
          });
        }
      }

      if (!validModel) {
        this.ShowToast(
          'Alerta',
          'Favor completar los servicios a solicitar.',
          'error'
        );
      }
      return params;
    },
    requestServiceComponentRef(componentName, idx) {
      let component = this.requestServiceRef.$refs[`${componentName}-${idx}`];
      return component != undefined ? component[0] : null;
    },
    setCollections(collections) {
      this.applicantCategories = [...collections.applicants].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      this.municipalities = collections.municipalities ?? [];
      this.documentTypes = collections.documentTypes ?? [];
      this.locationTypes = collections.locationTypes ?? [];
      this.unAvailableServices = collections.servicesNotAvailables ?? [];
    },
    onDownloadHandler({ documentId }) {
      if (documentId) {
        _downloadDocumentByAttachmentId(documentId);
      }
    },
    onFileDeletedHandler(item) {
      if (this.hasRequest) {
        this.deletedFiles.push(item);
      }
    },
  },
};
</script>

<style lang="scss">
.custom-tab-prov-remedy .custom-tab-container .nav-item .nav-link.active {
  border-bottom: 3px solid #ff7058 !important;
  color: #ff7058 !important;
}

.custom-board-prov-remedy > div > div.bg-white {
  background-color: transparent !important;
}

.title-bread-crumb-prov-remedy mb-3 > div > div.tag-badge {
  background: none !important;
}

.request-general-info-main-section .custom-disabled-label > div.input-display {
  margin-left: auto !important;
}

@media screen and (max-width: 768px) {
  div.actions button {
    width: 90% !important;
    padding: 10px !important;
  }

  div.actions {
    padding-right: 0 !important;
  }

  div.actions div {
    width: 100% !important;
  }

  div.actions.mobile-actions.col-xl-4 {
    display: flex;
    width: 100%;
  }

  label {
    margin-top: 1rem !important;
  }

  div.bg-light.rounded.p-4 {
    padding: 10px 0px 10px 0px !important;
  }

  button.btn.bg-light-white.w-75.pt-1 {
    width: 100% !important;
  }
}
</style>
